import { useEffect, useRef, useState } from 'react';
import { useStore } from '../../store';
import {
  CHAT_WINDOWS_STORAGE_KEY,
  NEW_MESSAGE_WINDOW,
} from '../../store/slices/chatWindows';
import { restoredWindowIds } from './chatFocus';
import { useInbox } from './useChatQueries';

export { CHAT_WINDOWS_STORAGE_KEY };

interface SavedChatWindows {
  dockOpen: boolean;
  open: string[];
  minimized: string[];
}

const isIdList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(id => typeof id === 'string');

/** null on missing, junk, or a throwing sessionStorage. */
export function readSavedChatWindows(): SavedChatWindows | null {
  try {
    const raw = sessionStorage.getItem(CHAT_WINDOWS_STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (
      typeof value?.dockOpen === 'boolean' &&
      isIdList(value.open) &&
      isIdList(value.minimized)
    )
      return {
        dockOpen: value.dockOpen,
        open: value.open,
        minimized: value.minimized,
      };
  } catch {
    // Blocked storage or junk JSON: start from the default state.
  }
  return null;
}

/**
 * Keeps the window layout across a reload, in sessionStorage only. The saved
 * layout is applied once, when the inbox first answers, so ids the inbox no
 * longer returns are dropped. Nothing is written before that: writing the
 * default state first would erase the saved layout.
 */
export function useChatWindowPersistence(): void {
  const saved = useRef<SavedChatWindows | null | undefined>(undefined);
  if (saved.current === undefined) saved.current = readSavedChatWindows();
  const [restored, setRestored] = useState(false);
  const dockOpenAtMount = useRef(useStore.getState().chatWindows.dockOpen);
  const inbox = useInbox();
  const dockOpen = useStore(state => state.chatWindows.dockOpen);
  const open = useStore(state => state.chatWindows.open);
  const minimized = useStore(state => state.chatWindows.minimized);

  useEffect(() => {
    if (restored || !inbox.data) return;
    const known = new Set(inbox.data.map(row => row.channel_id));
    const keep = (ids: string[]) =>
      ids.filter(id => id !== NEW_MESSAGE_WINDOW && known.has(id));
    const state = saved.current;
    const store = useStore.getState();
    // A chart entry or a person who opened a window, a tab or the dock before
    // the inbox answered wins.
    if (
      state &&
      store.chatWindows.open.length === 0 &&
      store.chatWindows.minimized.length === 0 &&
      store.chatWindows.dockOpen === dockOpenAtMount.current
    ) {
      const { chatSheetOpen, chatSheetView } = store;
      // Each call puts its id first, so apply back to front to keep the saved
      // order. Minimized first: opening can push an overflow window onto the
      // tab stack, and the saved tabs should sit below it.
      for (const id of keep(state.minimized).reverse()) {
        store.openChatWindow(id);
        store.minimizeChatWindow(id);
      }
      for (const id of keep(state.open).reverse()) {
        // A restore is not an open: the composer must not take focus.
        if (store.chatLayout === 'tray') restoredWindowIds.add(id);
        store.openChatWindow(id);
      }
      // Ids that overflowed to tabs at a smaller maxOpen never mount a
      // ConversationBody, so nothing would consume their mark.
      const stillOpen = new Set(useStore.getState().chatWindows.open);
      for (const id of [...restoredWindowIds])
        if (!stillOpen.has(id)) restoredWindowIds.delete(id);
      store.setChatDockOpen(state.dockOpen);
      // In the sheet layout opening a window also opens the phone sheet; a
      // reload must not.
      useStore.setState({ chatSheetOpen, chatSheetView });
    }
    setRestored(true);
  }, [restored, inbox.data]);

  useEffect(() => {
    if (!restored) return;
    try {
      sessionStorage.setItem(
        CHAT_WINDOWS_STORAGE_KEY,
        JSON.stringify({ dockOpen, open, minimized })
      );
    } catch {
      // Blocked storage: the layout just does not survive a reload.
    }
  }, [restored, dockOpen, open, minimized]);
}
